import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Loader2, RefreshCw, ToggleLeft, ToggleRight, Trash2, Plus, Send, Search, Filter, Pencil, X, Sparkles, ChevronDown, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { purchasingRulesService, type PurchasingRuleRow, type PromotionRow, type PurchasingRuleChatResponse } from '@/services/purchasingRulesService';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useCurrency } from '@/hooks/useCurrency';

type TabId = 'reglas' | 'promociones';

interface PendingRuleDraft {
  rule_type: string;
  rule_category: string;
  scope_type: string;
  scope_id?: number | null;
  scope_supplier_name?: string;
  scope_family_description?: string;
  parameters_json: Record<string, unknown>;
  priority: number;
  adjustment_strategy: string;
  active: boolean;
  valid_from?: string | null;
  valid_until?: string | null;
  priority_criterion?: string | null;
  description_for_user?: string;
  organization_id: number;
}

interface CopilotMessage {
  role: 'user' | 'assistant';
  text: string;
  pendingRules?: PendingRuleDraft[];
  saved?: boolean;
}

const CATEGORY_PILL: Record<string, string> = {
  objective: 'bg-blue-50 text-blue-700 border-blue-200',
  logistic_constraint: 'bg-amber-50 text-amber-700 border-amber-200',
  inventory_limit: 'bg-rose-50 text-rose-700 border-rose-200',
};

function MultiSelectDropdown({ label, options, selected, onChange, disabled }: {
  label: string;
  options: Array<{ id: number; name: string }>;
  selected: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const toggle = (id: number) => {
    if (disabled) return;
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const filtered = search
    ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => { setOpen(!open); if (open) setSearch(''); }}
        className="w-full flex items-center justify-between gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white hover:border-gray-400 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="truncate text-gray-700">
          {selected.length === 0 ? label : `${label} (${selected.length})`}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-1.5 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="w-full pl-6 pr-2 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none focus:border-stocker-primary"
              />
            </div>
          </div>
          <div className="max-h-44 overflow-y-auto">
            {filtered.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">Sin resultados</p>}
            {filtered.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => toggle(o.id)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 ${selected.includes(o.id) ? 'bg-stocker-primary/5 text-stocker-primary font-medium' : 'text-gray-700'}`}
              >
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${selected.includes(o.id) ? 'bg-stocker-primary border-stocker-primary' : 'border-gray-300'}`}>
                  {selected.includes(o.id) && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                <span className="truncate">{o.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PolicyCopilotSidebar({ open, onClose, warehouseId, onRulesSaved, allowEdit }: {
  open: boolean;
  onClose: () => void;
  warehouseId: number | null;
  onRulesSaved: () => void;
  allowEdit: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { symbol: currencySymbol } = useCurrency();
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Upload Excel packaging (pallets/bases) - solo subida (sin descargas)
  const [packagingModalOpen, setPackagingModalOpen] = useState(false);
  const [packagingUploading, setPackagingUploading] = useState(false);
  const [packagingFile, setPackagingFile] = useState<File | null>(null);
  const [packagingError, setPackagingError] = useState<string | null>(null);
  const [packagingSupplierId, setPackagingSupplierId] = useState<number | null>(null);

  const [suppliers, setSuppliers] = useState<Array<{ id: number; name: string }>>([]);
  const [families, setFamilies] = useState<Array<{ id: number; name: string }>>([]);
  const [selSuppliers, setSelSuppliers] = useState<number[]>([]);
  const [selFamilies, setSelFamilies] = useState<number[]>([]);
  const [productCodes, setProductCodes] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (open) {
      setIsAnimating(true);
      Promise.all([
        purchasingRulesService.getSuppliers(warehouseId ?? undefined),
        purchasingRulesService.getFamilies(warehouseId ?? undefined),
      ]).then(([s, f]) => { setSuppliers(s); setFamilies(f); }).catch(() => {});
    }
  }, [open, warehouseId]);

  useEffect(() => {
    if (!packagingModalOpen) return;
    setPackagingError(null);
    setPackagingFile(null);
    // Si el usuario tiene exactamente un proveedor seleccionado en el contexto, usamos ese.
    setPackagingSupplierId(selSuppliers.length === 1 ? selSuppliers[0] : null);
  }, [packagingModalOpen, selSuppliers]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const autoGrow = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }, []);

  const buildContextPrefix = () => {
    const parts: string[] = [];
    if (selSuppliers.length > 0) {
      const names = selSuppliers.map(id => suppliers.find(s => s.id === id)?.name).filter(Boolean);
      if (names.length) parts.push(`Proveedores: ${names.join(', ')}`);
    }
    if (selFamilies.length > 0) {
      const names = selFamilies.map(id => families.find(f => f.id === id)?.name).filter(Boolean);
      if (names.length) parts.push(`Familias: ${names.join(', ')}`);
    }
    if (productCodes.trim()) parts.push(`Productos: ${productCodes.trim()}`);
    return parts.length ? `[Contexto: ${parts.join(' | ')}]\n` : '';
  };

  const handleSend = async () => {
    if (!allowEdit) return;
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    if (textareaRef.current) { textareaRef.current.style.height = 'auto'; }

    const contextPrefix = buildContextPrefix();
    const fullMessage = contextPrefix + text;

    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const data: PurchasingRuleChatResponse = await purchasingRulesService.chat(
        fullMessage,
        false,
        warehouseId ?? undefined,
        false,
        undefined,
        i18n.language
      );

      const drafts: PendingRuleDraft[] = [];
      if (data.pending_rules && Array.isArray(data.pending_rules) && data.pending_rules.length > 0) {
        for (const r of data.pending_rules) drafts.push(r as unknown as PendingRuleDraft);
      } else if (data.pending_rule) {
        drafts.push(data.pending_rule as unknown as PendingRuleDraft);
      }

      if (drafts.length > 0) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: data.reply_text || '',
          pendingRules: drafts,
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: data.reply_text || data.interpretation?.reason || 'No se pudo interpretar.',
        }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', text: `Error: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRules = async (msgIndex: number) => {
    if (!allowEdit) return;
    const msg = messages[msgIndex];
    if (!msg?.pendingRules?.length || msg.saved) return;
    setSaving(true);
    try {
      let allOk = true;
      for (const rule of msg.pendingRules) {
        const res = await purchasingRulesService.chat(
          'sí',
          false,
          warehouseId ?? undefined,
          true,
          rule as unknown as Record<string, unknown>,
          i18n.language
        );
        if (!res.persisted) allOk = false;
      }
      if (allOk) {
        setMessages([{ role: 'assistant', text: t('businessRules.copilotSaved') || 'Políticas guardadas correctamente. Puedes crear nuevas políticas.' }]);
        onRulesSaved();
      } else {
        setMessages(prev => prev.map((m, i) => i === msgIndex ? { ...m, saved: true, text: m.text + '\n(Algunas reglas no se guardaron)' } : m));
      }
    } catch (e) {
      setMessages(prev => prev.map((m, i) => i === msgIndex ? { ...m, text: m.text + `\nError: ${e instanceof Error ? e.message : String(e)}` } : m));
    } finally {
      setSaving(false);
    }
  };

  const handleUploadPackagingExcel = async () => {
    if (!allowEdit) return;
    if (!packagingFile) return;
    if (packagingSupplierId == null) {
      setPackagingError(t('businessRules.selectSupplier') || 'Selecciona un proveedor');
      return;
    }
    setPackagingError(null);
    setPackagingUploading(true);
    try {
      const res = await purchasingRulesService.uploadPackagingExcel(packagingSupplierId, packagingFile);
      setPackagingModalOpen(false);
      setPackagingFile(null);
      // Dejar constancia en el chat.
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: t('businessRules.packagingUploaded', { count: res.rows_imported }) || `Packaging importado: ${res.rows_imported} filas.`,
        },
      ]);
      onRulesSaved();
    } catch (e: unknown) {
      setPackagingError(e instanceof Error ? e.message : 'Error al subir el Excel de packaging');
    } finally {
      setPackagingUploading(false);
    }
  };

  const ruleTypeLabel = (rt: string) => {
    const key = `businessRules.ruleType.${rt}`;
    const val = t(key);
    return val !== key ? val : rt.replace(/_/g, ' ');
  };

  const fmtParams = (params: Record<string, unknown>) => {
    const entries: string[] = [];
    Object.entries(params).forEach(([k, v]) => {
      if (v == null || v === '' || v === false) return;
      const paramKey = `businessRules.ruleParams.${k}`;
      if (v === true) {
        const lbl = t(paramKey);
        entries.push(lbl !== paramKey ? lbl : k.replace(/_/g, ' '));
        return;
      }
      if (typeof v === 'number') {
        const lbl = t(paramKey, { count: v, currencySymbol });
        entries.push(lbl !== paramKey ? lbl : `${k}: ${v.toLocaleString()}`);
        return;
      }
      entries.push(`${k}: ${String(v)}`);
    });
    return entries;
  };

  const categoryLabel = (cat: string) => {
    const c = (cat || '').toLowerCase();
    if (c === 'objective') return t('businessRules.categoryObjective');
    if (c === 'logistic_constraint') return t('businessRules.categoryLogistic');
    if (c === 'inventory_limit') return t('businessRules.categoryInventoryLimit');
    return t('businessRules.categoryOther');
  };

  if (!open) return null;

  return createPortal(
    <>
      <div className={`fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
      <div className={`fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col transition-transform duration-300 ${isAnimating ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-stocker-primary shrink-0" />
              <h3 className="text-sm font-semibold text-gray-900">{t('businessRules.newPolicy') || 'Nueva Política'}</h3>
            </div>
            {!allowEdit && (
              <p className="text-[11px] text-amber-700">{t('businessRules.editPolicyDisabled')}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                type="button"
                onClick={() => { setMessages([]); setInput(''); }}
                disabled={!allowEdit}
                className="p-1 rounded hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={t('businessRules.copilotNewChat') || 'Nueva conversación'}
              >
                <Plus className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-200 transition-colors"><X className="w-5 h-5 text-gray-500" /></button>
          </div>
        </div>

        {packagingModalOpen && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
            onClick={() => !packagingUploading && setPackagingModalOpen(false)}
          >
            <div
              className="bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium text-gray-900 mb-3">{t('businessRules.uploadPackagingExcel')}</h3>
              <p className="text-sm text-gray-500 mb-3">{t('businessRules.uploadPackagingHint')}</p>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('businessRules.selectSupplier')}</label>
                  <select
                    value={packagingSupplierId ?? ''}
                    onChange={(e) => setPackagingSupplierId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary"
                    disabled={packagingUploading}
                  >
                    <option value="">{t('businessRules.selectSupplier')}</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('businessRules.selectFile')}</label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setPackagingFile(e.target.files?.[0] ?? null)}
                    disabled={packagingUploading}
                    className="w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-gray-50"
                  />
                </div>

                {packagingError && (
                  <p className="text-sm text-red-600" role="alert">{packagingError}</p>
                )}
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPackagingModalOpen(false)}
                  disabled={packagingUploading}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleUploadPackagingExcel}
                  disabled={packagingUploading || !packagingFile}
                  className="px-3 py-2 rounded-lg bg-stocker-primary text-white text-sm font-medium hover:bg-stocker-primaryHover disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {packagingUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {packagingUploading ? t('common.loading') : t('common.upload')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Context filters */}
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{t('businessRules.copilotContext') || 'Contexto'}</p>
            {(selSuppliers.length > 0 || selFamilies.length > 0 || productCodes.trim()) && (
              <button
                type="button"
                disabled={!allowEdit}
                onClick={() => { setSelSuppliers([]); setSelFamilies([]); setProductCodes(''); }}
                className="text-[10px] text-gray-400 hover:text-red-500 transition-colors flex items-center gap-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X className="w-3 h-3" />
                {t('businessRules.copilotClearFilters') || 'Limpiar filtros'}
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <MultiSelectDropdown
              label={t('businessRules.scopeLabelSupplier') || 'Proveedores'}
              options={suppliers}
              selected={selSuppliers}
              onChange={setSelSuppliers}
              disabled={!allowEdit}
            />
            <MultiSelectDropdown
              label={t('businessRules.scopeLabelFamily') || 'Familias'}
              options={families}
              selected={selFamilies}
              onChange={setSelFamilies}
              disabled={!allowEdit}
            />
            <div>
              <input
                type="text"
                value={productCodes}
                onChange={(e) => setProductCodes(e.target.value)}
                placeholder={t('businessRules.copilotProductCodes') || 'Códigos producto...'}
                disabled={!allowEdit}
                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          {(selSuppliers.length > 0 || selFamilies.length > 0 || productCodes.trim()) && (
            <div className="flex flex-wrap gap-1 mt-2">
              {selSuppliers.map(id => {
                const s = suppliers.find(x => x.id === id);
                return s ? <span key={`s-${id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-[10px] text-indigo-700 border border-indigo-200">
                  {s.name}{' '}
                  <button type="button" disabled={!allowEdit} onClick={() => setSelSuppliers(prev => prev.filter(x => x !== id))} className="hover:text-indigo-900 disabled:opacity-40 disabled:cursor-not-allowed"><X className="w-3 h-3" /></button>
                </span> : null;
              })}
              {selFamilies.map(id => {
                const f = families.find(x => x.id === id);
                return f ? <span key={`f-${id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-50 text-[10px] text-teal-700 border border-teal-200">
                  {f.name}{' '}
                  <button type="button" disabled={!allowEdit} onClick={() => setSelFamilies(prev => prev.filter(x => x !== id))} className="hover:text-teal-900 disabled:opacity-40 disabled:cursor-not-allowed"><X className="w-3 h-3" /></button>
                </span> : null;
              })}
              {productCodes.trim() && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-50 text-[10px] text-violet-700 border border-violet-200">
                  {productCodes.trim()}{' '}
                  <button type="button" disabled={!allowEdit} onClick={() => setProductCodes('')} className="hover:text-violet-900 disabled:opacity-40 disabled:cursor-not-allowed"><X className="w-3 h-3" /></button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
              <Sparkles className="w-8 h-8 mb-2 text-gray-300" />
              <p className="text-sm">{t('businessRules.copilotEmpty') || 'Describe las políticas que necesitas crear'}</p>
              <p className="text-xs mt-1 text-gray-300">{t('businessRules.copilotHint') || 'Ej: "Pedido mínimo de 1500 unidades para el proveedor X"'}</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div className={msg.role === 'user'
                ? 'max-w-[85%] rounded-2xl rounded-br-md bg-stocker-primary text-white px-3 py-2 text-sm'
                : 'max-w-[90%] rounded-2xl rounded-bl-md bg-gray-100 text-gray-800 px-3 py-2 text-sm'
              }>
                {msg.role === 'user' ? (
                  <p className="whitespace-pre-wrap">{msg.text}</p>
                ) : (
                  <>
                    {msg.text && (
                      <div className="markdown-response text-sm [&_strong]:font-semibold [&_p]:my-0.5">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                      </div>
                    )}
                    {msg.pendingRules && msg.pendingRules.length > 0 && (
                      <div className="mt-2 space-y-2">
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="px-2 py-1.5 text-left font-medium text-gray-500">{t('businessRules.rulesTableCategory')}</th>
                                <th className="px-2 py-1.5 text-left font-medium text-gray-500">{t('businessRules.rulesTableType')}</th>
                                <th className="px-2 py-1.5 text-left font-medium text-gray-500">{t('businessRules.rulesTableScope')}</th>
                                <th className="px-2 py-1.5 text-left font-medium text-gray-500">{t('businessRules.rulesTableParameters')}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {msg.pendingRules.map((r, ri) => {
                                const cat = (r.rule_category || '').toLowerCase();
                                return (
                                  <tr key={ri} className="hover:bg-gray-50/50">
                                    <td className="px-2 py-1.5">
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${CATEGORY_PILL[cat] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                        {categoryLabel(cat) || cat || '—'}
                                      </span>
                                    </td>
                                    <td className="px-2 py-1.5 text-gray-700">{ruleTypeLabel(r.rule_type)}</td>
                                    <td className="px-2 py-1.5 text-gray-600">
                                      {(() => {
                                        const st = (r.scope_type || '').toLowerCase();
                                        const scopeBadge = st === 'supplier'
                                          ? 'bg-indigo-50 text-indigo-600 border-indigo-200'
                                          : st === 'product_family'
                                            ? 'bg-teal-50 text-teal-600 border-teal-200'
                                            : st === 'product'
                                              ? 'bg-violet-50 text-violet-600 border-violet-200'
                                              : 'bg-gray-50 text-gray-500 border-gray-200';
                                        const scopeLabel = st === 'supplier'
                                          ? (t('businessRules.scopeLabelSupplier') || 'Proveedor')
                                          : st === 'product_family'
                                            ? (t('businessRules.scopeLabelFamily') || 'Familia')
                                            : st === 'product'
                                              ? (t('businessRules.scopeLabelProduct') || 'Producto')
                                              : st;
                                        const scopeName = r.scope_supplier_name || r.scope_family_description || (r.scope_id != null ? `ID ${r.scope_id}` : '');
                                        return (
                                          <div className="flex flex-col gap-0.5">
                                            <span className={`inline-flex self-start items-center px-1 py-0 rounded text-[9px] font-medium border ${scopeBadge}`}>{scopeLabel}</span>
                                            {scopeName && <span className="text-[11px] text-gray-700 font-medium truncate max-w-[160px]" title={scopeName}>{scopeName}</span>}
                                          </div>
                                        );
                                      })()}
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <div className="flex flex-wrap gap-1">
                                        {fmtParams(r.parameters_json || {}).map((e, ei) => (
                                          <span key={ei} className="inline-flex px-1.5 py-0.5 rounded bg-gray-100 text-[10px] text-gray-700 border border-gray-200">{e}</span>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {!msg.saved && (
                          <div className="flex justify-end gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => setMessages(prev => prev.filter((_, idx) => idx !== i))}
                              disabled={!allowEdit}
                              className="px-2.5 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t('common.cancel') || 'Descartar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleConfirmRules(i)}
                              disabled={saving || !allowEdit}
                              className="px-2.5 py-1.5 rounded-lg bg-stocker-primary text-white text-xs font-medium hover:bg-stocker-primaryHover disabled:opacity-50 inline-flex items-center gap-1"
                            >
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              {saving ? (t('common.loading') || 'Guardando...') : (t('businessRules.copilotConfirm') || 'Guardar políticas')}
                            </button>
                          </div>
                        )}
                        {msg.saved && (
                          <div className="flex items-center gap-1 text-xs text-green-600 font-medium">
                            <Check className="w-3.5 h-3.5" />
                            {t('businessRules.copilotSaved') || 'Políticas guardadas correctamente'}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 flex items-center gap-2 text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('chat.thinking') || 'Pensando...'}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 px-4 py-3 bg-white">
          <div className="flex items-end gap-2 rounded-xl border border-gray-200 bg-white shadow-sm hover:border-stocker-primary/50 focus-within:border-stocker-primary focus-within:ring-2 focus-within:ring-stocker-primary/20 transition-all">
            <button
              type="button"
              onClick={() => {
                setPackagingError(null);
                setPackagingModalOpen(true);
              }}
              disabled={loading || saving || !allowEdit}
              className="p-2 ml-1 mb-1 rounded-full text-gray-500 hover:text-stocker-primary hover:bg-stocker-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('businessRules.attachPackaging') || 'Adjuntar Excel packaging'}
            >
              <Plus className="w-4 h-4" />
            </button>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => { setInput(e.target.value); autoGrow(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={t('businessRules.copilotPlaceholder') || 'Describe las políticas que quieres crear...'}
              rows={1}
              className="flex-1 py-2.5 px-3 text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent resize-none min-h-[2.5rem] max-h-[160px] overflow-y-auto"
              disabled={loading || saving || !allowEdit}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={loading || saving || !input.trim() || !allowEdit}
              className="p-2 mr-1 mb-1 rounded-full bg-stocker-primary text-white hover:bg-stocker-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

export function BusinessAssistantPage() {
  const { t, i18n } = useTranslation();
  const { format: formatMoney, symbol: currencySymbol } = useCurrency();
  const warehouseId = useWarehouseId();
  const { isActionEnabled } = useFeatureVisibility();
  const canEditRules = isActionEnabled('business_rules.edit_rule', true);
  const [activeTab, setActiveTab] = useState<TabId>('reglas');

  const [rules, setRules] = useState<PurchasingRuleRow[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [suppliers, setSuppliers] = useState<Array<{ id: number; name: string }>>([]);
  const [families, setFamilies] = useState<Array<{ id: number; name: string }>>([]);
  const [rulesSearch, setRulesSearch] = useState('');
  const [rulesCategoryFilter, setRulesCategoryFilter] = useState<string>('all');
  const [rulesScopeFilter, setRulesScopeFilter] = useState<string>('all');

  const [promotions, setPromotions] = useState<PromotionRow[]>([]);
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [promotionsUploading, setPromotionsUploading] = useState(false);
  const [promotionsFile, setPromotionsFile] = useState<File | null>(null);
  const [promotionsError, setPromotionsError] = useState<string | null>(null);
  const [promotionsModalOpen, setPromotionsModalOpen] = useState(false);
  const [deletingPromotionId, setDeletingPromotionId] = useState<string | null>(null);
  const [promoSearch, setPromoSearch] = useState('');
  const [promoScopeFilter, setPromoScopeFilter] = useState<string>('all');
  const [editingPromo, setEditingPromo] = useState<PromotionRow | null>(null);
  const [editPromoForm, setEditPromoForm] = useState<{
    discount_pct: string; start_date: string; end_date: string; minimum_volume: string; promotion_price: string;
  }>({ discount_pct: '', start_date: '', end_date: '', minimum_volume: '', promotion_price: '' });
  const [editPromoSaving, setEditPromoSaving] = useState(false);
  const [editPromoError, setEditPromoError] = useState<string | null>(null);

  const [copilotOpen, setCopilotOpen] = useState(false);

  useEffect(() => {
    if (!canEditRules && copilotOpen) setCopilotOpen(false);
  }, [canEditRules, copilotOpen]);

  const loadRules = async () => {
    setRulesLoading(true);
    try {
      const list = await purchasingRulesService.listRules(false);
      setRules(list);
    } catch (e) {
      console.error(e);
    } finally {
      setRulesLoading(false);
    }
  };

  const loadPromotions = async () => {
    setPromotionsLoading(true);
    try {
      const list = await purchasingRulesService.listPromotions(false);
      setPromotions(list);
    } catch (e) {
      console.error(e);
    } finally {
      setPromotionsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'reglas') loadRules();
  }, [activeTab]);
  useEffect(() => {
    if (activeTab === 'promociones') loadPromotions();
  }, [activeTab]);

  const handleToggleActive = async (r: PurchasingRuleRow) => {
    if (!canEditRules) return;
    try {
      await purchasingRulesService.updateRule(r.id, { active: !r.active });
      loadRules();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id: number) => {
    if (!canEditRules) return;
    if (!window.confirm(t('businessRules.confirmDelete'))) return;
    try {
      await purchasingRulesService.deleteRule(id);
      loadRules();
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (activeTab === 'reglas') {
      Promise.all([
        purchasingRulesService.getSuppliers(warehouseId ?? undefined),
        purchasingRulesService.getFamilies(warehouseId ?? undefined),
      ]).then(([s, f]) => {
        setSuppliers(s);
        setFamilies(f);
      }).catch(() => {});
    }
  }, [activeTab, warehouseId]);

  const assignScope = async (ruleId: number, scopeId: number, scopeType: 'supplier' | 'product_family') => {
    if (!canEditRules) return;
    setAssigningId(ruleId);
    try {
      await purchasingRulesService.updateRule(ruleId, {
        scope_id: scopeId,
        ...(scopeType === 'supplier' ? { scope_supplier_name: '' } : { scope_family_description: '' }),
      });
      loadRules();
    } finally {
      setAssigningId(null);
    }
  };

  const handlePromotionsUpload = async () => {
    if (!canEditRules) return;
    if (!promotionsFile) return;
    setPromotionsError(null);
    setPromotionsUploading(true);
    try {
      const res = await purchasingRulesService.uploadPromotionsExcel(promotionsFile);
      if (res.errors?.length) {
        setPromotionsError(res.errors.slice(0, 5).join(' '));
      }
      setPromotionsModalOpen(false);
      setPromotionsFile(null);
      await loadPromotions();
    } catch (e: unknown) {
      setPromotionsError(e instanceof Error ? e.message : 'Error al subir');
    } finally {
      setPromotionsUploading(false);
    }
  };

  const handleDeletePromotion = async (id: string) => {
    if (!canEditRules) return;
    setDeletingPromotionId(id);
    try {
      await purchasingRulesService.deletePromotion(id);
      setPromotions((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingPromotionId(null);
    }
  };

  const openEditPromo = (p: PromotionRow) => {
    if (!canEditRules) return;
    setEditingPromo(p);
    setEditPromoForm({
      discount_pct: String(p.discount_pct ?? ''),
      start_date: p.start_date ?? '',
      end_date: p.end_date ?? '',
      minimum_volume: p.minimum_volume != null ? String(p.minimum_volume) : '',
      promotion_price: p.promotion_price != null ? String(p.promotion_price) : '',
    });
    setEditPromoError(null);
  };

  const handleSavePromo = async () => {
    if (!canEditRules || !editingPromo) return;
    setEditPromoSaving(true);
    setEditPromoError(null);
    try {
      const updates: Record<string, unknown> = {};
      const disc = parseFloat(editPromoForm.discount_pct);
      if (!Number.isNaN(disc)) updates.discount_pct = disc;
      if (editPromoForm.start_date) updates.start_date = editPromoForm.start_date;
      if (editPromoForm.end_date) updates.end_date = editPromoForm.end_date;
      const mv = editPromoForm.minimum_volume ? parseInt(editPromoForm.minimum_volume, 10) : null;
      updates.minimum_volume = mv;
      const pp = editPromoForm.promotion_price ? parseFloat(editPromoForm.promotion_price) : null;
      updates.promotion_price = pp;
      const updated = await purchasingRulesService.updatePromotion(editingPromo.id, updates as Partial<PromotionRow>);
      setPromotions((prev) => prev.map((p) => p.id === updated.id ? updated : p));
      setEditingPromo(null);
    } catch (e) {
      setEditPromoError(e instanceof Error ? e.message : String(e));
    } finally {
      setEditPromoSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="border-b border-gray-200 px-4">
        <div className="flex gap-6">
          <button
            type="button"
            onClick={() => setActiveTab('reglas')}
            className={`py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'reglas'
                ? 'border-stocker-primary text-stocker-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('businessRules.tabRules')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('promociones')}
            className={`py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'promociones'
                ? 'border-stocker-primary text-stocker-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('businessRules.tabPromotions')}
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0">
        {activeTab === 'reglas' && (() => {
          const catPill = (cat: string) => {
            const c = (cat || '').toLowerCase();
            if (c === 'objective') return 'bg-blue-50 text-blue-700 border-blue-200';
            if (c === 'logistic_constraint') return 'bg-amber-50 text-amber-700 border-amber-200';
            if (c === 'inventory_limit') return 'bg-rose-50 text-rose-700 border-rose-200';
            return 'bg-gray-50 text-gray-600 border-gray-200';
          };
          const catLabel = (cat: string) => {
            const c = (cat || '').toLowerCase();
            if (c === 'objective') return t('businessRules.categoryObjective');
            if (c === 'logistic_constraint') return t('businessRules.categoryLogistic');
            if (c === 'inventory_limit') return t('businessRules.categoryInventoryLimit');
            return t('businessRules.categoryOther');
          };
          const ruleTypeLabel = (rt: string) => {
            const k = `businessRules.ruleType.${rt}`;
            return t(k) !== k ? t(k) : rt.replace(/_/g, ' ');
          };
          const scopeName = (r: PurchasingRuleRow) => {
            const st = (r.scope_type || '').toLowerCase();
            if (st === 'supplier') {
              const name = r.scope_supplier_name_display || r.scope_supplier_name || '';
              if (name) return name;
              if (r.scope_id != null) {
                const s = suppliers.find(s => s.id === r.scope_id);
                return s ? s.name : `ID ${r.scope_id}`;
              }
              return t('businessRules.unassigned');
            }
            if (st === 'product_family') {
              const name = r.scope_family_name_display || r.scope_family_description || '';
              if (name) return name;
              if (r.scope_id != null) {
                const f = families.find(f => f.id === r.scope_id);
                return f ? f.name : `ID ${r.scope_id}`;
              }
              return t('businessRules.unassigned');
            }
            if (st === 'product') {
              const prodName = r.scope_product_name_display || '';
              const prodCode = r.scope_product_code_display || '';
              if (prodName && prodCode) return `${prodName} (${prodCode})`;
              if (prodName) return prodName;
              if (prodCode) return prodCode;
              return r.scope_id != null ? `ID ${r.scope_id}` : '—';
            }
            if (st === 'organization') return t('businessRules.scopeLabelOrganization') || 'Organización';
            return r.scope_id != null ? `ID ${r.scope_id}` : '—';
          };
          const scopeLabel = (st: string) => {
            if (st === 'supplier') return t('businessRules.scopeLabelSupplier') || 'Proveedor';
            if (st === 'product_family') return t('businessRules.scopeLabelFamily') || 'Familia';
            if (st === 'product') return t('businessRules.scopeLabelProduct') || 'Producto';
            if (st === 'organization') return t('businessRules.scopeLabelOrganization') || 'Organización';
            if (st === 'warehouse') return t('businessRules.scopeLabelWarehouse') || 'Almacén';
            return st;
          };
          const formatParams = (params: Record<string, unknown>) => {
            const entries: string[] = [];
            const fmtVal = (v: unknown) => {
              if (typeof v === 'number') return v.toLocaleString();
              return String(v ?? '');
            };
            const paramLabels: Record<string, string> = {
              monthly_target: 'Objetivo mensual (u)',
              monthly_target_amount: `Objetivo mensual (${currencySymbol})`,
              annual_target: 'Objetivo anual (u)',
              annual_target_amount: `Objetivo anual (${currencySymbol})`,
              min_order_amount: `Pedido mín. (${currencySymbol})`,
              min_order_qty: 'Cantidad mín. (u)',
              minimum_order_quantity: 'Cantidad mín. (u)',
              minimum_pallets: 'Mín. pallets',
              minimum_bases: 'Mín. bases',
              round_to_full_pallets: 'Redondear a pallets',
              round_to_full_bases: 'Redondear a bases',
              max_stock: 'Stock máximo (u)',
              max_coverage_days: 'Cobertura máx. (días)',
              purchase_multiple: 'Múltiplo de compra',
            };
            Object.entries(params).forEach(([k, v]) => {
              if (v == null || v === '' || v === false) return;
              const label = paramLabels[k] || k.replace(/_/g, ' ');
              if (v === true) { entries.push(label); return; }
              entries.push(`${label}: ${fmtVal(v)}`);
            });
            return entries;
          };
          const formatDate = (iso: string | null | undefined) => {
            if (!iso) return null;
            try {
              const d = new Date(iso);
              if (Number.isNaN(d.getTime())) return iso;
              return d.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
            } catch { return iso; }
          };

          const search = rulesSearch.toLowerCase();
          const filtered = rules.filter(r => {
            if (rulesCategoryFilter !== 'all' && (r.rule_category || '').toLowerCase() !== rulesCategoryFilter) return false;
            if (rulesScopeFilter !== 'all' && r.scope_type !== rulesScopeFilter) return false;
            if (search) {
              const haystack = [
                ruleTypeLabel(r.rule_type),
                scopeName(r),
                scopeLabel(r.scope_type),
                ...formatParams(r.parameters_json || {}),
                r.description_for_user || '',
              ].join(' ').toLowerCase();
              if (!haystack.includes(search)) return false;
            }
            return true;
          });

          const categories = ['all', 'objective', 'logistic_constraint', 'inventory_limit', 'other'];
          const scopes = ['all', ...Array.from(new Set(rules.map(r => r.scope_type)))];

          return (
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="max-w-6xl mx-auto">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h2 className="text-lg font-medium text-gray-900">{t('businessRules.rulesTitle')}</h2>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={loadRules}
                      disabled={rulesLoading}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${rulesLoading ? 'animate-spin' : ''}`} />
                      {rulesLoading ? t('common.loading') : t('businessRules.refresh')}
                    </button>
                    <button
                      type="button"
                      onClick={() => canEditRules && setCopilotOpen(true)}
                      disabled={!canEditRules}
                      title={!canEditRules ? t('businessRules.editPolicyDisabled') : undefined}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-stocker-primary text-white text-sm font-medium hover:bg-stocker-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Sparkles className="w-4 h-4" />
                      {t('businessRules.newPolicy') || 'Nueva Política'}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className="relative flex-1 min-w-[200px] max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={rulesSearch}
                      onChange={(e) => setRulesSearch(e.target.value)}
                      placeholder={t('common.search') || 'Buscar...'}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary"
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                      value={rulesCategoryFilter}
                      onChange={(e) => setRulesCategoryFilter(e.target.value)}
                      className="text-sm border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary"
                    >
                      {categories.map(c => (
                        <option key={c} value={c}>{c === 'all' ? (t('businessRules.filterCategoryAll') || 'Categoría: Todas') : catLabel(c)}</option>
                      ))}
                    </select>
                    <select
                      value={rulesScopeFilter}
                      onChange={(e) => setRulesScopeFilter(e.target.value)}
                      className="text-sm border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary"
                    >
                      {scopes.map(s => (
                        <option key={s} value={s}>{s === 'all' ? (t('businessRules.filterScopeAll') || 'Ámbito: Todos') : scopeLabel(s)}</option>
                      ))}
                    </select>
                  </div>
                  <span className="text-xs text-gray-400 ml-auto">{filtered.length} / {rules.length}</span>
                </div>

                {filtered.length === 0 && !rulesLoading ? (
                  <p className="text-gray-500 text-sm py-8 text-center">{t('businessRules.noRules')}</p>
                ) : (
                  <div className="space-y-2">
                    {filtered.map((r) => {
                      const cat = (r.rule_category || '').toLowerCase();
                      const paramEntries = formatParams(r.parameters_json || {});
                      const validFrom = formatDate(r.valid_from);
                      const validUntil = formatDate(r.valid_until);
                      const hasValidity = validFrom || validUntil;
                      const isUnassigned = r.scope_id == null && (r.scope_type === 'supplier' || r.scope_type === 'product_family');

                      return (
                        <div
                          key={r.id}
                          className={`border rounded-lg p-3 transition-colors ${r.active ? 'bg-white border-gray-200 hover:border-gray-300' : 'bg-gray-50 border-gray-200 opacity-60'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${catPill(cat)}`}>
                                  {catLabel(cat)}
                                </span>
                                <span className="text-sm font-medium text-gray-800">
                                  {ruleTypeLabel(r.rule_type)}
                                </span>
                                {r.rule_type === 'frequency_adjustment' && (
                                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                    {t('businessRules.deprecated')}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1.5">
                                <span className="font-medium text-gray-600">{scopeLabel(r.scope_type)}:</span>
                                {isUnassigned ? (
                                  <span className="flex items-center gap-1">
                                    <span className="text-amber-600">{t('businessRules.unassigned')}</span>
                                    <select
                                      value=""
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (v) assignScope(r.id, Number(v), r.scope_type as 'supplier' | 'product_family');
                                      }}
                                      disabled={assigningId === r.id || !canEditRules}
                                      title={!canEditRules ? t('businessRules.editPolicyDisabled') : undefined}
                                      className="text-xs border border-gray-300 rounded px-1 py-0.5 max-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <option value="">{r.scope_type === 'supplier' ? t('businessRules.assignSupplier') : t('businessRules.assignFamily')}</option>
                                      {(r.scope_type === 'supplier' ? suppliers : families).map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                      ))}
                                    </select>
                                  </span>
                                ) : (
                                  <span className="text-gray-700">{scopeName(r)}</span>
                                )}
                              </div>

                              {paramEntries.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {paramEntries.map((entry, i) => (
                                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-[11px] text-gray-700 border border-gray-200">
                                      {entry}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {(hasValidity || r.priority_criterion) && (
                                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400">
                                  {hasValidity && (
                                    <span>
                                      {validFrom && validUntil ? `${validFrom} – ${validUntil}` : validFrom ? `Desde ${validFrom}` : `Hasta ${validUntil}`}
                                    </span>
                                  )}
                                  {r.priority_criterion && (
                                    <span>
                                      {t(`businessRules.priorityCriterionLabel.${r.priority_criterion}`) !== `businessRules.priorityCriterionLabel.${r.priority_criterion}`
                                        ? t(`businessRules.priorityCriterionLabel.${r.priority_criterion}`)
                                        : r.priority_criterion}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => handleToggleActive(r)}
                                disabled={!canEditRules}
                                className="p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                title={!canEditRules ? t('businessRules.editPolicyDisabled') : (r.active ? t('businessRules.deactivate') : t('businessRules.activate'))}
                              >
                                {r.active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(r.id)}
                                disabled={!canEditRules}
                                className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                title={!canEditRules ? t('businessRules.editPolicyDisabled') : t('common.delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {activeTab === 'promociones' && (() => {
          const promoScopeLabel = (st: string) => {
            if (st === 'supplier') return t('businessRules.scopeLabelSupplier') || 'Proveedor';
            if (st === 'product_family') return t('businessRules.scopeLabelFamily') || 'Familia';
            if (st === 'product') return t('businessRules.scopeLabelProduct') || 'Producto';
            return st;
          };
          const promoScopePill = (st: string) => {
            if (st === 'supplier') return 'bg-indigo-50 text-indigo-700 border-indigo-200';
            if (st === 'product_family') return 'bg-teal-50 text-teal-700 border-teal-200';
            return 'bg-violet-50 text-violet-700 border-violet-200';
          };
          const fmtPromoDate = (iso: string | null | undefined) => {
            if (!iso) return null;
            try {
              const d = new Date(iso);
              if (Number.isNaN(d.getTime())) return iso;
              return d.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
            } catch { return iso; }
          };
          const isActive = (p: PromotionRow) => {
            const now = new Date();
            if (p.start_date && new Date(p.start_date) > now) return false;
            if (p.end_date && new Date(p.end_date) < now) return false;
            return true;
          };

          const search = promoSearch.toLowerCase();
          const filtered = promotions.filter(p => {
            if (promoScopeFilter !== 'all' && p.scope_type !== promoScopeFilter) return false;
            if (search) {
              const haystack = [
                p.scope_code || '',
                p.scope_name || '',
                String(p.scope_id ?? ''),
                promoScopeLabel(p.scope_type),
                String(p.discount_pct),
                p.start_date || '',
                p.end_date || '',
              ].join(' ').toLowerCase();
              if (!haystack.includes(search)) return false;
            }
            return true;
          });

          const promoScopes = ['all', ...Array.from(new Set(promotions.map(p => p.scope_type)))];

          return (
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="max-w-6xl mx-auto">
                {promotionsModalOpen && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !promotionsUploading && setPromotionsModalOpen(false)}>
                    <div className="bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-4" onClick={(e) => e.stopPropagation()}>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">{t('businessRules.promotionsUploadExcel')}</h3>
                      <p className="text-sm text-gray-500 mb-3">{t('businessRules.promotionsUploadHint')}</p>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('businessRules.selectFile')}</label>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setPromotionsFile(e.target.files?.[0] ?? null)}
                        className="w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-gray-50"
                      />
                      {promotionsError && (
                        <p className="mt-2 text-sm text-red-600" role="alert">{promotionsError}</p>
                      )}
                      <div className="mt-4 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setPromotionsModalOpen(false)}
                          disabled={promotionsUploading}
                          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {t('common.cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={handlePromotionsUpload}
                          disabled={promotionsUploading || !promotionsFile}
                          className="px-3 py-2 rounded-lg bg-stocker-primary text-white text-sm font-medium hover:bg-stocker-primaryHover disabled:opacity-50 inline-flex items-center gap-2"
                        >
                          {promotionsUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                          {promotionsUploading ? t('common.loading') : t('common.upload')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h2 className="text-lg font-medium text-gray-900">{t('businessRules.promotionsTitle')}</h2>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={loadPromotions}
                      disabled={promotionsLoading}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${promotionsLoading ? 'animate-spin' : ''}`} />
                      {promotionsLoading ? t('common.loading') : t('businessRules.refresh')}
                    </button>
                    <button
                      type="button"
                      onClick={() => canEditRules && (setPromotionsError(null), setPromotionsModalOpen(true))}
                      disabled={!canEditRules}
                      title={!canEditRules ? t('businessRules.editPolicyDisabled') : undefined}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-stocker-primary text-white text-sm font-medium hover:bg-stocker-primaryHover disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4" />
                      {t('businessRules.promotionsUploadExcel')}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className="relative flex-1 min-w-[200px] max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={promoSearch}
                      onChange={(e) => setPromoSearch(e.target.value)}
                      placeholder={t('common.search') || 'Buscar...'}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary"
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                      value={promoScopeFilter}
                      onChange={(e) => setPromoScopeFilter(e.target.value)}
                      className="text-sm border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary"
                    >
                      {promoScopes.map(s => (
                        <option key={s} value={s}>{s === 'all' ? (t('businessRules.filterScopeAll') || 'Ámbito: Todos') : promoScopeLabel(s)}</option>
                      ))}
                    </select>
                  </div>
                  <span className="text-xs text-gray-400 ml-auto">{filtered.length} / {promotions.length}</span>
                </div>

                {editingPromo && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !editPromoSaving && setEditingPromo(null)}>
                    <div className="bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900">{t('businessRules.promotionsEdit') || 'Editar promoción'}</h3>
                        <button type="button" onClick={() => setEditingPromo(null)} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
                      </div>
                      <div className="mb-3 text-sm text-gray-600">
                        <span className="font-medium">{promoScopeLabel(editingPromo.scope_type)}:</span>{' '}
                        {editingPromo.scope_name ? `${editingPromo.scope_name} (${editingPromo.scope_code || editingPromo.scope_id})` : (editingPromo.scope_code || editingPromo.scope_id)}
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('businessRules.promotionsDiscount') || 'Descuento %'}</label>
                          <input type="number" step="0.1" value={editPromoForm.discount_pct} onChange={(e) => setEditPromoForm(f => ({ ...f, discount_pct: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('businessRules.promotionsStart') || 'Inicio'}</label>
                            <input type="date" value={editPromoForm.start_date} onChange={(e) => setEditPromoForm(f => ({ ...f, start_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('businessRules.promotionsEnd') || 'Fin'}</label>
                            <input type="date" value={editPromoForm.end_date} onChange={(e) => setEditPromoForm(f => ({ ...f, end_date: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('businessRules.promotionsMinVolume') || 'Vol. mín.'}</label>
                            <input type="number" value={editPromoForm.minimum_volume} onChange={(e) => setEditPromoForm(f => ({ ...f, minimum_volume: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary" placeholder="—" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('businessRules.promotionsPrice') || 'Precio promo'}</label>
                            <input type="number" step="0.01" value={editPromoForm.promotion_price} onChange={(e) => setEditPromoForm(f => ({ ...f, promotion_price: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-stocker-primary/20 focus:border-stocker-primary" placeholder="—" />
                          </div>
                        </div>
                      </div>
                      {editPromoError && <p className="mt-2 text-sm text-red-600">{editPromoError}</p>}
                      <div className="mt-4 flex justify-end gap-2">
                        <button type="button" onClick={() => setEditingPromo(null)} disabled={editPromoSaving} className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">{t('common.cancel')}</button>
                        <button type="button" onClick={handleSavePromo} disabled={editPromoSaving} className="px-3 py-2 rounded-lg bg-stocker-primary text-white text-sm font-medium hover:bg-stocker-primaryHover disabled:opacity-50 inline-flex items-center gap-2">
                          {editPromoSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                          {editPromoSaving ? t('common.loading') : (t('common.save') || 'Guardar')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {filtered.length === 0 && !promotionsLoading ? (
                  <p className="text-gray-500 text-sm py-8 text-center">{t('businessRules.promotionsNoData')}</p>
                ) : (
                  <div className="space-y-2">
                    {filtered.map((p) => {
                      const active = isActive(p);
                      const startFmt = fmtPromoDate(p.start_date);
                      const endFmt = fmtPromoDate(p.end_date);
                      const hasDate = startFmt || endFmt;
                      const displayName = p.scope_name || '';
                      const displayCode = p.scope_code || (p.scope_id != null ? String(p.scope_id) : '');

                      return (
                        <div
                          key={p.id}
                          className={`border rounded-lg p-3 transition-colors ${active ? 'bg-white border-gray-200 hover:border-gray-300' : 'bg-gray-50 border-gray-200 opacity-60'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${promoScopePill(p.scope_type)}`}>
                                  {promoScopeLabel(p.scope_type)}
                                </span>
                                <span className="text-sm font-medium text-gray-800">
                                  {displayName && displayCode ? `${displayName}` : displayName || displayCode || '—'}
                                </span>
                                {displayCode && displayName && (
                                  <span className="text-xs text-gray-400 font-mono">{displayCode}</span>
                                )}
                                {!active && (
                                  <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                    {t('businessRules.promotionsExpired') || 'Expirada'}
                                  </span>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {p.discount_pct > 0 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-green-50 text-[11px] text-green-700 border border-green-200 font-medium">
                                    {t('businessRules.promotionsDiscount') || 'Descuento'}: {p.discount_pct}%
                                  </span>
                                )}
                                {p.promotion_price != null && p.promotion_price > 0 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-[11px] text-blue-700 border border-blue-200 font-medium">
                                    {t('businessRules.promotionsPrice') || 'Precio promo'}: {formatMoney(p.promotion_price)}
                                  </span>
                                )}
                                {p.minimum_volume != null && p.minimum_volume > 0 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-[11px] text-gray-700 border border-gray-200">
                                    {t('businessRules.promotionsMinVolume') || 'Vol. mín.'}: {p.minimum_volume.toLocaleString()} u
                                  </span>
                                )}
                              </div>

                              {hasDate && (
                                <div className="flex items-center gap-1 mt-1.5 text-[11px] text-gray-400">
                                  <span>
                                    {startFmt && endFmt ? `${startFmt} – ${endFmt}` : startFmt ? `Desde ${startFmt}` : `Hasta ${endFmt}`}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => openEditPromo(p)}
                                disabled={!canEditRules}
                                className="p-1 rounded text-gray-400 hover:text-stocker-primary hover:bg-stocker-primary/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                title={!canEditRules ? t('businessRules.editPolicyDisabled') : (t('businessRules.promotionsEdit') || 'Editar')}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeletePromotion(p.id)}
                                disabled={deletingPromotionId === p.id || !canEditRules}
                                className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                                title={!canEditRules ? t('businessRules.editPolicyDisabled') : t('common.delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      <PolicyCopilotSidebar
        open={copilotOpen}
        onClose={() => setCopilotOpen(false)}
        warehouseId={warehouseId}
        onRulesSaved={() => { loadRules(); }}
        allowEdit={canEditRules}
      />
    </div>
  );
}
